import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { FiClock, FiAlertCircle } from 'react-icons/fi';

const PST_TZ = 'America/Los_Angeles';

export function utcToPstParts(utcIso) {
  if (!utcIso) return { date: '', time: '' };
  const d = new Date(utcIso);
  const dateStr = new Intl.DateTimeFormat('en-CA', { timeZone: PST_TZ, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
  const timeStr = new Intl.DateTimeFormat('en-GB', { timeZone: PST_TZ, hour: '2-digit', minute: '2-digit', hour12: false }).format(d);
  return { date: dateStr, time: timeStr };
}

export function pstPartsToUtc(dateStr, timeStr) {
  if (!dateStr || !timeStr) return null;
  const localStr = `${dateStr}T${timeStr}:00`;
  const localDate = new Date(localStr);
  const utcFromLocal = localDate.toLocaleString('en-US', { timeZone: 'UTC' });
  const pstFromLocal = localDate.toLocaleString('en-US', { timeZone: PST_TZ });
  const offsetMs = new Date(utcFromLocal) - new Date(pstFromLocal);
  return new Date(localDate.getTime() + offsetMs).toISOString();
}

export function formatPstDisplay(utcIso) {
  if (!utcIso) return '';
  return new Intl.DateTimeFormat('en-US', {
    timeZone: PST_TZ,
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  }).format(new Date(utcIso)) + ' PST';
}

export function formatPstShort(utcIso) {
  if (!utcIso) return '';
  return new Intl.DateTimeFormat('en-US', {
    timeZone: PST_TZ,
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
  }).format(new Date(utcIso));
}

export function formatPstDate(utcIso) {
  if (!utcIso) return '';
  return new Intl.DateTimeFormat('en-US', {
    timeZone: PST_TZ,
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
  }).format(new Date(utcIso));
}

export function formatPstTime(utcIso) {
  if (!utcIso) return '';
  return new Intl.DateTimeFormat('en-US', {
    timeZone: PST_TZ, hour: 'numeric', minute: '2-digit', hour12: true,
  }).format(new Date(utcIso));
}

export function getPstDayKey(utcIso) {
  if (!utcIso) return '';
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: PST_TZ, year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(new Date(utcIso));
}

export function isInPast(utcIso) {
  return utcIso && new Date(utcIso) < new Date();
}

export default function SchedulePicker({
  value,
  onChange,
  disabled = false,
  error = null,
  label = 'Schedule',
  className = '',
}) {
  const initialParts = useMemo(() => utcToPstParts(value), [value]);
  const [localDate, setLocalDate] = useState(initialParts.date);
  const [localTime, setLocalTime] = useState(initialParts.time);

  useEffect(() => {
    const parts = utcToPstParts(value);
    setLocalDate(parts.date);
    setLocalTime(parts.time);
  }, [value]);

  const todayPst = useMemo(() => {
    return new Intl.DateTimeFormat('en-CA', { timeZone: PST_TZ, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
  }, []);

  const handleDateChange = useCallback((newDate) => {
    setLocalDate(newDate);
    if (newDate && localTime) {
      onChange?.(pstPartsToUtc(newDate, localTime));
    } else if (!newDate) {
      onChange?.(null);
    }
  }, [localTime, onChange]);

  const handleTimeChange = useCallback((newTime) => {
    setLocalTime(newTime);
    if (localDate && newTime) {
      onChange?.(pstPartsToUtc(localDate, newTime));
    } else if (!newTime) {
      onChange?.(null);
    }
  }, [localDate, onChange]);

  const isPast = value && isInPast(value);

  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-zinc-700 mb-1.5">{label}</label>
      )}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <input
            type="date"
            value={localDate}
            min={todayPst}
            onChange={(e) => handleDateChange(e.target.value)}
            disabled={disabled}
            className={`
              w-full px-3 py-2.5 rounded-xl border text-sm transition-all
              focus:outline-none focus:ring-2 focus:ring-offset-0
              ${error || isPast
                ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20'
                : 'border-zinc-200 focus:border-indigo-500 focus:ring-indigo-500/20 hover:border-zinc-300'
              }
              ${disabled ? 'bg-zinc-50 text-zinc-400 cursor-not-allowed' : 'bg-white text-zinc-900'}
            `}
          />
        </div>
        <div className="relative flex-1">
          <input
            type="time"
            value={localTime}
            onChange={(e) => handleTimeChange(e.target.value)}
            disabled={disabled}
            className={`
              w-full px-3 py-2.5 rounded-xl border text-sm transition-all
              focus:outline-none focus:ring-2 focus:ring-offset-0
              ${error || isPast
                ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20'
                : 'border-zinc-200 focus:border-indigo-500 focus:ring-indigo-500/20 hover:border-zinc-300'
              }
              ${disabled ? 'bg-zinc-50 text-zinc-400 cursor-not-allowed' : 'bg-white text-zinc-900'}
            `}
          />
        </div>
        <div className="flex items-center gap-1 px-2.5 py-2.5 rounded-xl bg-zinc-100 text-zinc-500 text-xs font-medium whitespace-nowrap">
          <FiClock className="w-3.5 h-3.5" />
          PST
        </div>
      </div>

      {(error || isPast) && (
        <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
          <FiAlertCircle className="w-3 h-3" />
          {error || 'Scheduled time is in the past'}
        </p>
      )}

      {value && !isPast && !error && (
        <p className="mt-1.5 text-xs text-zinc-500">
          {formatPstDisplay(value)}
        </p>
      )}
    </div>
  );
}
